import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { 
  UploadCloud, Download, Save, Users, BookOpen, CheckCircle, 
  Filter, Search, ArrowRight, AlertTriangle, Info, RefreshCw, 
  Trash, UserCheck, X, Check, Loader, Copy, Grid, Zap, Play,
  Pause, FileText, TrendingUp, Database
} from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Modal from '../../components/ui/Modal';
import { debug } from '../../utils/debug';
import { useStabilizedGrid, useStabilizedAllocation } from '../../contexts/StabilizedContexts';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { supabase, DB_SCHEMA } from '../../supabase/config';
import { staffService } from '../../services/staffService';

interface BulkAllocation {
  teacherId: string;
  teacherName: string;
  teacherCode: string;
  classId: string;
  className: string;
  lineId: string;
  lineName: string;
  curriculumArea: string;
  yearLevel: string;
  status: 'pending' | 'valid' | 'invalid' | 'warning' | 'processing' | 'completed' | 'failed';
  message?: string;
}

interface BulkOperation {
  type: 'assign' | 'clear' | 'swap';
  items: BulkAllocation[];
  status: 'pending' | 'processing' | 'completed' | 'failed';
  timestamp: Date;
}

const BulkOperations: React.FC = () => {
  const { addToast } = useToast();
  const confirm = useConfirm();
  const { user } = useAuth();
  const { activeGrid, grids } = useStabilizedGrid();
  const { batchAssignTeachers, assignTeacher } = useStabilizedAllocation();
  
  // Staff data state
  const [staff, setStaff] = useState<any[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  
  // State management
  const [bulkItems, setBulkItems] = useState<BulkAllocation[]>([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [processingBulk, setProcessingBulk] = useState(false);
  const [processingProgress, setProcessingProgress] = useState<{current: number; total: number} | null>(null);
  const [selectedOperation, setSelectedOperation] = useState<'assign' | 'clear'>('assign');
  const [operationHistory, setOperationHistory] = useState<BulkOperation[]>([]);
  
  // Selection state for building allocations
  const [selectedTeacherId, setSelectedTeacherId] = useState<string>('');
  const [selectedClassId, setSelectedClassId] = useState<string>('');
  const [selectedLineId, setSelectedLineId] = useState<string>('');

  // Load staff data from Supabase
  useEffect(() => {
    const loadStaff = async () => {
      if (!user) return;
      
      setDataLoading(true);
      try {
        const staffData = await staffService.getAllStaff();
        setStaff(staffData || []);
      } catch (error) {
        console.error('Error loading staff:', error);
        addToast('Failed to load staff data', 'error');
        setStaff([]);
      } finally {
        setDataLoading(false);
      }
    };

    loadStaff();
  }, [user, addToast]);

  // Real-time staff updates
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('bulk_operations_staff')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: DB_SCHEMA,
          table: 'staff',
        },
        () => {
          debug('Staff data changed, refreshing...');
          const loadStaff = async () => {
            try {
              const staffData = await staffService.getAllStaff();
              setStaff(staffData || []);
            } catch (error) {
              console.error('Error refreshing staff:', error);
            }
          };
          loadStaff();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Get available teachers, classes, and lines
  const availableTeachers = useMemo(() => {
    return staff.filter(s => s.status === 'active');
  }, [staff]);

  const availableClasses = useMemo(() => {
    if (!activeGrid) return [];
    return activeGrid.classes;
  }, [activeGrid]);

  const availableLines = useMemo(() => {
    if (!activeGrid) return [];
    return activeGrid.lines;
  }, [activeGrid]);

  // Get unassigned classes for quick selection
  const unassignedClasses = useMemo(() => {
    if (!activeGrid) return [];
    return activeGrid.classes.filter(cls => {
      // Check if any line has no assignment
      return activeGrid.lines.some(line => 
        !cls.lineAssignments || !cls.lineAssignments[line.id]
      );
    });
  }, [activeGrid]);

  // Generate validation stats
  const validationStats = useMemo(() => {
    const total = bulkItems.length;
    const valid = bulkItems.filter(item => item.status === 'valid').length;
    const warnings = bulkItems.filter(item => item.status === 'warning').length;
    const invalid = bulkItems.filter(item => item.status === 'invalid').length;
    const pending = bulkItems.filter(item => item.status === 'pending').length;
    const completed = bulkItems.filter(item => item.status === 'completed').length;
    
    return { total, valid, warnings, invalid, pending, completed };
  }, [bulkItems]);

  // Filter bulk items
  const filteredBulkItems = useMemo(() => {
    return bulkItems.filter(item => {
      // Status filter
      if (filterStatus !== 'all' && item.status !== filterStatus) return false;
      
      // Search query
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        return (
          item.teacherName.toLowerCase().includes(searchLower) ||
          item.className.toLowerCase().includes(searchLower) ||
          item.curriculumArea.toLowerCase().includes(searchLower) ||
          item.teacherCode.toLowerCase().includes(searchLower)
        );
      }
      
      return true;
    });
  }, [bulkItems, filterStatus, searchQuery]);

  // Handle adding a new allocation
  const handleAddAllocation = useCallback(() => {
    if (!selectedTeacherId || !selectedClassId || !selectedLineId) {
      addToast('Please select a teacher, class, and line', 'error');
      return;
    }

    const teacher = staff.find(s => s.id === selectedTeacherId);
    const classData = availableClasses.find(c => c.id === selectedClassId);
    const lineData = availableLines.find(l => l.id === selectedLineId);

    if (!teacher || !classData || !lineData) {
      addToast('Invalid selection', 'error');
      return;
    }

    // Check if this allocation already exists
    const existingIndex = bulkItems.findIndex(
      item => item.teacherId === selectedTeacherId && 
              item.classId === selectedClassId && 
              item.lineId === selectedLineId
    );

    if (existingIndex !== -1) {
      addToast('This allocation is already in the list', 'warning');
      return;
    }

    // Create new allocation
    const newAllocation: BulkAllocation = {
      teacherId: selectedTeacherId,
      teacherName: teacher.name,
      teacherCode: teacher.staff_id,
      classId: selectedClassId,
      className: classData.className,
      lineId: selectedLineId,
      lineName: lineData.name,
      curriculumArea: classData.curriculumArea || 'Unknown',
      yearLevel: classData.yearLevel || 'Unknown',
      status: 'pending'
    };

    setBulkItems([...bulkItems, newAllocation]);
    
    // Reset selections
    setSelectedTeacherId('');
    setSelectedClassId('');
    setSelectedLineId('');
    
    // Validate the new allocation
    validateAllocation(newAllocation);
  }, [selectedTeacherId, selectedClassId, selectedLineId, bulkItems, staff, availableClasses, availableLines, addToast]);

  // Validate a single allocation
  const validateAllocation = (allocation: BulkAllocation) => {
    const teacher = staff.find(s => s.id === allocation.teacherId);
    const classData = availableClasses.find(c => c.id === allocation.classId);
    
    if (!teacher || !classData) {
      allocation.status = 'invalid';
      allocation.message = 'Invalid teacher or class';
      return;
    }

    // Check if already assigned
    const existingAssignment = classData.lineAssignments?.[allocation.lineId];
    if (existingAssignment && selectedOperation === 'assign') {
      allocation.status = 'warning';
      allocation.message = `Will replace existing assignment: ${existingAssignment}`;
    } else {
      allocation.status = 'valid';
      allocation.message = undefined;
    }

    // Update the item in the list
    setBulkItems(current => 
      current.map(item => 
        item.teacherId === allocation.teacherId && 
        item.classId === allocation.classId && 
        item.lineId === allocation.lineId
          ? allocation
          : item
      )
    );
  };

  // Validate all allocations
  const validateAllAllocations = useCallback(() => {
    bulkItems.forEach(item => {
      validateAllocation(item);
    });
  }, [bulkItems]);

  // Handle CSV import
  const handleImportCSV = useCallback((csvText: string) => {
    try {
      const lines = csvText.split('\n').filter(line => line.trim());
      const header = lines[0].toLowerCase().split(',').map(h => h.trim());
      
      // Find column indices
      const teacherCodeIndex = header.findIndex(h => h === 'teachercode' || h === 'teacher');
      const classIdIndex = header.findIndex(h => h === 'classid' || h === 'class');
      const lineIdIndex = header.findIndex(h => h === 'lineid' || h === 'line');
      
      if (teacherCodeIndex === -1 || classIdIndex === -1 || lineIdIndex === -1) {
        throw new Error('CSV must include TeacherCode, ClassId, and LineId columns');
      }

      const newItems: BulkAllocation[] = [];
      
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        const values = line.split(',').map(v => v.trim());
        const teacherCode = values[teacherCodeIndex];
        const classId = values[classIdIndex];
        const lineId = values[lineIdIndex];
        
        const teacher = staff.find(s => s.staff_id === teacherCode);
        const classData = availableClasses.find(c => c.id === classId);
        const lineData = availableLines.find(l => l.id === lineId);
        
        if (!teacher || !classData || !lineData) continue;
        
        newItems.push({
          teacherId: teacher.id,
          teacherName: teacher.name,
          teacherCode: teacher.staff_id,
          classId,
          className: classData.className,
          lineId,
          lineName: lineData.name,
          curriculumArea: classData.curriculumArea || 'Unknown',
          yearLevel: classData.yearLevel || 'Unknown',
          status: 'pending'
        });
      }

      setBulkItems([...bulkItems, ...newItems]);
      setShowImportModal(false);
      
      // Validate the new allocations
      setTimeout(() => {
        newItems.forEach(item => validateAllocation(item));
      }, 100);
      
      addToast(`Imported ${newItems.length} allocations`, 'success');
    } catch (error) {
      addToast(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    }
  }, [staff, availableClasses, availableLines, bulkItems, addToast]);

  // Handle removing an allocation
  const handleRemoveAllocation = useCallback((item: BulkAllocation) => {
    setBulkItems(current => 
      current.filter(i => 
        !(i.teacherId === item.teacherId && 
          i.classId === item.classId && 
          i.lineId === item.lineId)
      )
    );
  }, []);

  // Process allocations with progress tracking
  const handleProcessAllocations = useCallback(async () => {
    setProcessingBulk(true);
    setProcessingProgress({ current: 0, total: bulkItems.length });
    
    try {
      // Filter valid allocations
      const validAllocations = bulkItems.filter(
        item => item.status === 'valid' || item.status === 'warning'
      );
      
      if (validAllocations.length === 0) {
        addToast('No valid allocations to process', 'warning');
        setProcessingBulk(false);
        setProcessingProgress(null);
        return;
      }

      // Process based on operation type
      if (selectedOperation === 'assign') {
        // Use batch assignment for better performance
        const assignments = validAllocations.map(item => ({
          classId: item.classId,
          lineId: item.lineId,
          teacherCode: item.teacherCode
        }));

        // Update progress
        setProcessingProgress({ current: 0, total: assignments.length });

        // Process in batches of 10 for visual feedback
        const batchSize = 10;
        for (let i = 0; i < assignments.length; i += batchSize) {
          const batch = assignments.slice(i, Math.min(i + batchSize, assignments.length));
          await batchAssignTeachers(batch);
          
          // Update progress
          setProcessingProgress({
            current: Math.min(i + batchSize, assignments.length),
            total: assignments.length
          });

          // Update status of processed items
          const processedIds = batch.map(b => `${b.classId}-${b.lineId}`);
          setBulkItems(current => 
            current.map(item => {
              const itemId = `${item.classId}-${item.lineId}`;
              if (processedIds.includes(itemId)) {
                return { ...item, status: 'completed' };
              }
              return item;
            })
          );
        }

        addToast(`Successfully assigned ${assignments.length} teachers`, 'success');
      } else if (selectedOperation === 'clear') {
        // Clear assignments
        const clearOperations = validAllocations.map(item => ({
          classId: item.classId,
          lineId: item.lineId,
          teacherCode: null
        }));

        await batchAssignTeachers(clearOperations);
        addToast(`Cleared ${clearOperations.length} assignments`, 'success');
      }

      // Add to history
      const operation: BulkOperation = {
        type: selectedOperation,
        items: validAllocations,
        status: 'completed',
        timestamp: new Date()
      };
      setOperationHistory([operation, ...operationHistory.slice(0, 9)]);

      // Clear processed items
      setBulkItems([]);
      setShowConfirmModal(false);
      
    } catch (error) {
      addToast('Failed to process allocations', 'error');
      console.error('Bulk operation error:', error);
    } finally {
      setProcessingBulk(false);
      setProcessingProgress(null);
    }
  }, [bulkItems, selectedOperation, batchAssignTeachers, operationHistory, addToast]);

  // Generate template CSV
  const generateTemplateCSV = () => {
    const header = 'TeacherCode,ClassId,LineId,Notes\n';
    const sampleRows = availableClasses.slice(0, 3).map(cls => {
      const line = availableLines[0];
      const teacher = availableTeachers[0];
      return `${teacher?.staff_id || 'ABC'},${cls.id},${line?.id || 'line1'},Example allocation`;
    }).join('\n');
    
    const blob = new Blob([header + (sampleRows || 'ABC,class1,line1,Example')], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_allocation_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Export current allocations
  const exportCurrentAllocations = () => {
    const header = 'TeacherCode,TeacherName,ClassId,ClassName,LineId,LineName,Status,Message\n';
    const rows = bulkItems.map(item => 
      `${item.teacherCode},${item.teacherName},${item.classId},${item.className},${item.lineId},${item.lineName},${item.status},${item.message || ''}`
    ).join('\n');
    
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk_allocations_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Quick actions
  const handleAddAllUnassigned = useCallback(() => {
    const newAllocations: BulkAllocation[] = [];
    
    unassignedClasses.forEach(cls => {
      activeGrid?.lines.forEach(line => {
        if (!cls.lineAssignments?.[line.id]) {
          // Try to find a suitable teacher (this is a simple example)
          const suitableTeacher = availableTeachers[0]; // You'd want smarter logic here
          
          if (suitableTeacher) {
            newAllocations.push({
              teacherId: suitableTeacher.id,
              teacherName: suitableTeacher.name,
              teacherCode: suitableTeacher.staff_id,
              classId: cls.id,
              className: cls.className,
              lineId: line.id,
              lineName: line.name,
              curriculumArea: cls.curriculumArea || 'Unknown',
              yearLevel: cls.yearLevel || 'Unknown',
              status: 'pending'
            });
          }
        }
      });
    });

    setBulkItems([...bulkItems, ...newAllocations]);
    addToast(`Added ${newAllocations.length} unassigned slots`, 'info');
  }, [unassignedClasses, activeGrid, availableTeachers, bulkItems, addToast]);

  // Status badge component
  const getStatusBadge = (status: string) => {
    const badges = {
      valid: { icon: CheckCircle, color: 'green', text: 'Valid' },
      warning: { icon: AlertTriangle, color: 'yellow', text: 'Warning' },
      invalid: { icon: X, color: 'red', text: 'Invalid' },
      pending: { icon: RefreshCw, color: 'gray', text: 'Pending' },
      processing: { icon: Loader, color: 'blue', text: 'Processing' },
      completed: { icon: Check, color: 'green', text: 'Completed' },
      failed: { icon: X, color: 'red', text: 'Failed' }
    };

    const badge = badges[status as keyof typeof badges] || badges.pending;
    const Icon = badge.icon;

    // Static token-class lookup. Building class names dynamically
    // (`bg-${color}-100`) would be purged by Tailwind and never render, so each
    // colour maps to a full, literal token class string here.
    const badgeClasses: Record<string, string> = {
      green: 'bg-ok-bg text-ok-fg',
      yellow: 'bg-warn-bg text-warn-fg',
      red: 'bg-danger-bg text-danger-fg',
      blue: 'bg-info-bg text-info-fg',
      gray: 'bg-ink-100 text-ink-700',
    };
    const colorClass = badgeClasses[badge.color] || badgeClasses.gray;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        <Icon className="h-3 w-3 mr-1" />
        {badge.text}
      </span>
    );
  };

  if (dataLoading || !activeGrid) {
    return (
      <div>
        <PageHeader 
          title="Bulk Operations" 
          description="Manage multiple teacher allocations at once"
        />
        <Card>
          <div className="p-12 text-center">
            <Loader className="h-8 w-8 text-navy-500 animate-spin mx-auto mb-4" />
            <h3 className="text-card-title text-ink-900">Loading...</h3>
            <p className="mt-2 text-sm text-ink-500">
              Preparing bulk operations
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Bulk Operations"
        description="Efficiently manage multiple teacher allocations at once"
        actions={
          <div className="flex space-x-2">
            <Button
              variant="secondary"
              leftIcon={<Download className="h-4 w-4" />}
              onClick={generateTemplateCSV}
            >
              Template
            </Button>
            <Button
              variant="secondary"
              leftIcon={<UploadCloud className="h-4 w-4" />}
              onClick={() => setShowImportModal(true)}
            >
              Import CSV
            </Button>
            <Button
              variant="primary"
              leftIcon={<Save className="h-4 w-4" />}
              onClick={() => {
                if (bulkItems.length === 0) {
                  addToast('No allocations to process', 'warning');
                } else {
                  setShowConfirmModal(true);
                }
              }}
              disabled={bulkItems.length === 0 || validationStats.valid + validationStats.warnings === 0}
            >
              Process {validationStats.valid + validationStats.warnings} Items
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <div className="p-4">
              <div className="flex items-center">
                <Grid className="h-8 w-8 text-navy-500 mr-3" />
                <div>
                  <p className="text-sm text-ink-500">Total Classes</p>
                  <p className="font-mono tabular-nums text-2xl font-bold">{availableClasses.length}</p>
                </div>
              </div>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <div className="flex items-center">
                <Users className="h-8 w-8 text-ok-fg mr-3" />
                <div>
                  <p className="text-sm text-ink-500">Available Teachers</p>
                  <p className="font-mono tabular-nums text-2xl font-bold">{availableTeachers.length}</p>
                </div>
              </div>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <div className="flex items-center">
                <AlertTriangle className="h-8 w-8 text-warn-fg mr-3" />
                <div>
                  <p className="text-sm text-ink-500">Unassigned Slots</p>
                  <p className="font-mono tabular-nums text-2xl font-bold">{unassignedClasses.length}</p>
                </div>
              </div>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <div className="flex items-center">
                <TrendingUp className="h-8 w-8 text-navy-500 mr-3" />
                <div>
                  <p className="text-sm text-ink-500">Pending Items</p>
                  <p className="font-mono tabular-nums text-2xl font-bold">{bulkItems.length}</p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Operation Type Selection */}
        <Card>
          <div className="p-4 border-b border-ink-200">
            <h3 className="text-card-title text-ink-900">Operation Type</h3>
          </div>
          <div className="p-4">
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="assign"
                  checked={selectedOperation === 'assign'}
                  onChange={(e) => setSelectedOperation(e.target.value as 'assign')}
                  className="mr-2"
                />
                <UserCheck className="h-4 w-4 mr-1" />
                Assign Teachers
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="clear"
                  checked={selectedOperation === 'clear'}
                  onChange={(e) => setSelectedOperation(e.target.value as 'clear')}
                  className="mr-2"
                />
                <Trash className="h-4 w-4 mr-1" />
                Clear Assignments
              </label>
            </div>
          </div>
        </Card>

        {/* Allocation Builder */}
        <Card>
          <div className="p-4 border-b border-ink-200">
            <div className="flex justify-between items-center">
              <h3 className="text-card-title text-ink-900">
                Allocation Builder
              </h3>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Zap className="h-4 w-4" />}
                onClick={handleAddAllUnassigned}
              >
                Add All Unassigned
              </Button>
            </div>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {selectedOperation === 'assign' && (
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    Teacher
                  </label>
                  <select
                    className="block w-full pl-3 pr-10 py-2 text-base border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm rounded-md"
                    value={selectedTeacherId}
                    onChange={(e) => setSelectedTeacherId(e.target.value)}
                  >
                    <option value="">Select teacher...</option>
                    {availableTeachers.map(teacher => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.staff_id} - {teacher.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">
                  Class
                </label>
                <select
                  className="block w-full pl-3 pr-10 py-2 text-base border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm rounded-md"
                  value={selectedClassId}
                  onChange={(e) => setSelectedClassId(e.target.value)}
                >
                  <option value="">Select class...</option>
                  {availableClasses.map(cls => (
                    <option key={cls.id} value={cls.id}>
                      {cls.className} ({cls.curriculumArea})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">
                  Line
                </label>
                <select
                  className="block w-full pl-3 pr-10 py-2 text-base border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm rounded-md"
                  value={selectedLineId}
                  onChange={(e) => setSelectedLineId(e.target.value)}
                >
                  <option value="">Select line...</option>
                  {availableLines.map(line => (
                    <option key={line.id} value={line.id}>
                      {line.name} ({line.semester})
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-end">
                <Button
                  variant="primary"
                  leftIcon={selectedOperation === 'assign' ? <UserCheck className="h-4 w-4" /> : <Trash className="h-4 w-4" />}
                  onClick={handleAddAllocation}
                  disabled={
                    (selectedOperation === 'assign' && (!selectedTeacherId || !selectedClassId || !selectedLineId)) ||
                    (selectedOperation === 'clear' && (!selectedClassId || !selectedLineId))
                  }
                >
                  Add to List
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Bulk Allocations List */}
        <Card>
          <div className="p-4 border-b border-ink-200">
            <div className="flex justify-between items-center">
              <h3 className="text-card-title text-ink-900">
                Pending Operations ({bulkItems.length})
              </h3>
              <div className="flex space-x-2">
                {bulkItems.length > 0 && (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<FileText className="h-4 w-4" />}
                      onClick={exportCurrentAllocations}
                    >
                      Export
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<RefreshCw className="h-4 w-4" />}
                      onClick={validateAllAllocations}
                    >
                      Validate All
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Trash className="h-4 w-4" />}
                      onClick={async () => {
                        if (await confirm({
                          title: 'Clear all operations?',
                          message: 'Clear all pending operations?',
                          confirmLabel: 'Clear all',
                          variant: 'danger',
                        })) {
                          setBulkItems([]);
                        }
                      }}
                    >
                      Clear All
                    </Button>
                  </>
                )}
              </div>
            </div>
            
            {/* Stats */}
            {bulkItems.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                <div className="flex items-center px-3 py-1 rounded-full bg-ink-100 text-ink-900 text-xs">
                  <span className="font-medium">Total:</span>
                  <span className="font-mono tabular-nums ml-1">{validationStats.total}</span>
                </div>
                {validationStats.valid > 0 && (
                  <div className="flex items-center px-3 py-1 rounded-full bg-ok-bg text-ok-fg text-xs">
                    <Check className="h-3 w-3 mr-1" />
                    <span className="font-medium">Valid:</span>
                    <span className="font-mono tabular-nums ml-1">{validationStats.valid}</span>
                  </div>
                )}
                {validationStats.warnings > 0 && (
                  <div className="flex items-center px-3 py-1 rounded-full bg-warn-bg text-warn-fg text-xs">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    <span className="font-medium">Warnings:</span>
                    <span className="font-mono tabular-nums ml-1">{validationStats.warnings}</span>
                  </div>
                )}
                {validationStats.invalid > 0 && (
                  <div className="flex items-center px-3 py-1 rounded-full bg-danger-bg text-danger-fg text-xs">
                    <X className="h-3 w-3 mr-1" />
                    <span className="font-medium">Invalid:</span>
                    <span className="font-mono tabular-nums ml-1">{validationStats.invalid}</span>
                  </div>
                )}
                {validationStats.completed > 0 && (
                  <div className="flex items-center px-3 py-1 rounded-full bg-navy-100 text-navy-800 text-xs">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    <span className="font-medium">Completed:</span>
                    <span className="font-mono tabular-nums ml-1">{validationStats.completed}</span>
                  </div>
                )}
              </div>
            )}
            
            {/* Search and Filter */}
            {bulkItems.length > 0 && (
              <div className="mt-4 flex space-x-2">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-ink-400" />
                  </div>
                  <input
                    type="text"
                    className="block w-full pl-10 pr-3 py-2 border border-ink-300 rounded-md leading-5 bg-white placeholder-ink-500 focus:outline-none focus:ring-1 focus:ring-navy-500 focus:border-navy-500 sm:text-sm"
                    placeholder="Search allocations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <select
                  className="block w-40 pl-3 pr-10 py-2 text-base border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm rounded-md"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="valid">Valid</option>
                  <option value="warning">Warnings</option>
                  <option value="invalid">Invalid</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            {filteredBulkItems.length === 0 ? (
              <div className="text-center py-12">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-ink-100">
                  <Info className="h-6 w-6 text-ink-500" />
                </div>
                <h3 className="mt-2 text-sm font-medium text-ink-900">
                  No Operations Pending
                </h3>
                <p className="mt-1 text-sm text-ink-500">
                  Add allocations using the form above or import from CSV.
                </p>
                <div className="mt-6 flex justify-center space-x-3">
                  <Button variant="secondary" onClick={() => setShowImportModal(true)}>
                    Import from CSV
                  </Button>
                  <Button variant="secondary" onClick={() => setShowTemplateModal(true)}>
                    View Template
                  </Button>
                </div>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-ink-200">
                <thead className="bg-ink-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                      Teacher
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                      Class
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                      Line
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                      Message
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-ink-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-ink-200">
                  {filteredBulkItems.map((item, index) => (
                    <tr key={`${item.teacherId}-${item.classId}-${item.lineId}-${index}`} className="hover:bg-ink-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-mono tabular-nums text-sm font-medium text-ink-900">
                          {item.teacherCode}
                        </div>
                        <div className="text-xs text-ink-500">
                          {item.teacherName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-mono tabular-nums text-sm text-ink-900">
                          {item.className}
                        </div>
                        <div className="text-xs text-ink-500">
                          {item.curriculumArea} • {item.yearLevel}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-mono tabular-nums text-sm text-ink-900">
                          {item.lineName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(item.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-ink-500">
                          {item.message || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          className="text-danger-fg hover:text-danger-fg"
                          onClick={() => handleRemoveAllocation(item)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>

        {/* Processing Progress */}
        {processingProgress && (
          <Card>
            <div className="p-6">
              <h3 className="text-card-title text-ink-900 mb-4">
                Processing Operations
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between text-sm text-ink-500">
                  <span>Progress</span>
                  <span className="font-mono tabular-nums">{processingProgress.current} of {processingProgress.total}</span>
                </div>
                <div className="w-full bg-ink-200 rounded-full h-2">
                  <div
                    className="bg-navy-500 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${(processingProgress.current / processingProgress.total) * 100}%`
                    }}
                  />
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Import Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Import Allocations from CSV"
        footer={
          <div className="flex justify-between w-full">
            <Button
              variant="secondary"
              leftIcon={<Download className="h-4 w-4" />}
              onClick={generateTemplateCSV}
            >
              Download Template
            </Button>
            <Button variant="secondary" onClick={() => setShowImportModal(false)}>
              Cancel
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="bg-navy-50 p-4 rounded-md">
            <div className="flex">
              <Info className="h-5 w-5 text-navy-400 mr-2" />
              <div>
                <h3 className="text-sm font-medium text-navy-800">CSV Format</h3>
                <p className="text-sm text-navy-700 mt-1">
                  Your CSV file should include: TeacherCode, ClassId, LineId
                </p>
              </div>
            </div>
          </div>
          
          <div className="border-2 border-dashed border-ink-300 rounded-md p-6">
            <div className="text-center">
              <UploadCloud className="mx-auto h-12 w-12 text-ink-400" />
              <div className="mt-2">
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <span className="mt-2 block text-sm font-medium text-ink-700">
                    Upload a CSV file
                  </span>
                  <input
                    id="csv-upload"
                    name="csv-upload"
                    type="file"
                    accept=".csv"
                    className="sr-only"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (e) => {
                          const text = e.target?.result as string;
                          if (text) {
                            handleImportCSV(text);
                          }
                        };
                        reader.readAsText(file);
                      }
                    }}
                  />
                </label>
                <p className="mt-1 text-xs text-ink-500">
                  Click to select a file or drag and drop
                </p>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Confirm Modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={`Confirm Bulk ${selectedOperation === 'assign' ? 'Assignment' : 'Clear'}`}
        footer={
          <div className="flex justify-end space-x-2">
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              leftIcon={<Save className="h-4 w-4" />}
              onClick={handleProcessAllocations}
              loading={processingBulk}
            >
              Process {validationStats.valid + validationStats.warnings} Operations
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="bg-warn-bg p-4 rounded-md">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-warn-solid mr-2" />
              <div>
                <h3 className="text-sm font-medium text-warn-fg">
                  Confirmation Required
                </h3>
                <p className="text-sm text-warn-fg mt-1">
                  You are about to {selectedOperation === 'assign' ? 'assign' : 'clear'}{' '}
                  {validationStats.valid + validationStats.warnings} allocations.
                  This action cannot be undone.
                </p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-b py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-medium text-ink-500">Valid operations:</span>
                <span className="font-mono tabular-nums ml-2 text-sm text-ink-900">{validationStats.valid}</span>
              </div>
              <div>
                <span className="text-sm font-medium text-ink-500">Warnings:</span>
                <span className="font-mono tabular-nums ml-2 text-sm text-ink-900">{validationStats.warnings}</span>
              </div>
              <div>
                <span className="text-sm font-medium text-ink-500">Invalid (will be skipped):</span>
                <span className="font-mono tabular-nums ml-2 text-sm text-ink-900">{validationStats.invalid}</span>
              </div>
              <div>
                <span className="text-sm font-medium text-ink-500">Total to process:</span>
                <span className="font-mono tabular-nums ml-2 text-sm font-medium text-navy-600">
                  {validationStats.valid + validationStats.warnings}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BulkOperations;